import { useCallback, useEffect, useMemo, useState } from 'react'
import { TaskActualWorkApproveUserDto } from '../../../../../../applications/ports/taskActualWorkApproveRepository'
import { intl } from '../../../../../../i18n'

export type FilterInfo = {
  tabIndex: number
  hasDivisionFilter: boolean
  hasTeamFilter: boolean
  isFilterActive: boolean
  filterLabel: string
}

export const createDefaultFilterInfo = (): FilterInfo => ({
  tabIndex: 0,
  hasDivisionFilter: false,
  hasTeamFilter: false,
  isFilterActive: false,
  filterLabel: '',
})

const getFilterLabel = (tabIndex: number): string => {
  switch (tabIndex) {
    case 0:
      return intl.formatMessage({
        id: 'taskActualWorkApprove.filter.requesting',
      })
    case 1:
      return intl.formatMessage({
        id: 'taskActualWorkApprove.filter.unrequested',
      })
    case 2:
      return intl.formatMessage({ id: 'taskActualWorkApprove.filter.tab.all' })
    default:
      return ''
  }
}

interface UseFilterStateProps {
  allUsersWithStatus: TaskActualWorkApproveUserDto[]
  onFilterChange?: (filterInfo: FilterInfo) => void
  onFilteredUsersChange?: (users: TaskActualWorkApproveUserDto[]) => void
}

export const useFilterState = ({
  allUsersWithStatus,
  onFilterChange,
  onFilteredUsersChange,
}: UseFilterStateProps) => {
  const [tabIndex, setTabIndex] = useState<number>(2)
  const [selectedDivisionUuid, setSelectedDivisionUuid] = useState<
    string | undefined
  >()
  const [selectedTeamUuid, setSelectedTeamUuid] = useState<string | undefined>()

  const divisions = useMemo(() => {
    const divisionMap = new Map()
    allUsersWithStatus.forEach(user => {
      const div = user.projectMemberDetail.user.division
      if (div && !divisionMap.has(div.uuid)) {
        divisionMap.set(div.uuid, {
          uuid: div.uuid,
          displayName: div.displayName,
        })
      }
    })
    return Array.from(divisionMap.values())
  }, [allUsersWithStatus])

  const teams = useMemo(() => {
    const teamMap = new Map()
    allUsersWithStatus.forEach(user => {
      const team = user.projectMemberDetail.team
      if (team && !teamMap.has(team.uuid)) {
        teamMap.set(team.uuid, {
          uuid: team.uuid,
          displayName: team.displayName,
        })
      }
    })
    return Array.from(teamMap.values())
  }, [allUsersWithStatus])

  const filteredUsers = useMemo(() => {
    let result = allUsersWithStatus

    if (tabIndex === 0) {
      result = result.filter(u => u.requestingCount > 0)
    } else if (tabIndex === 1) {
      result = result.filter(u => u.unrequestedCount > 0)
    }

    if (selectedDivisionUuid) {
      result = result.filter(
        u => u.projectMemberDetail.user.division?.uuid === selectedDivisionUuid
      )
    }

    if (selectedTeamUuid) {
      result = result.filter(
        u => u.projectMemberDetail.team?.uuid === selectedTeamUuid
      )
    }

    return result.sort((a, b) =>
      (a.projectMemberDetail.user.name || '').localeCompare(
        b.projectMemberDetail.user.name || ''
      )
    )
  }, [allUsersWithStatus, tabIndex, selectedDivisionUuid, selectedTeamUuid])

  const requestingUsersCount = useMemo(() => {
    let result = allUsersWithStatus.filter(u => u.requestingCount > 0)

    if (selectedDivisionUuid) {
      result = result.filter(
        u => u.projectMemberDetail.user.division?.uuid === selectedDivisionUuid
      )
    }

    if (selectedTeamUuid) {
      result = result.filter(
        u => u.projectMemberDetail.team?.uuid === selectedTeamUuid
      )
    }

    return result.length
  }, [allUsersWithStatus, selectedDivisionUuid, selectedTeamUuid])

  const unrequestedUsersCount = useMemo(() => {
    return allUsersWithStatus.filter(u => u.unrequestedCount > 0).length
  }, [allUsersWithStatus])

  useEffect(() => {
    if (onFilterChange) {
      const isFilterActive =
        tabIndex !== 2 || !!selectedDivisionUuid || !!selectedTeamUuid
      onFilterChange({
        tabIndex,
        hasDivisionFilter: !!selectedDivisionUuid,
        hasTeamFilter: !!selectedTeamUuid,
        isFilterActive,
        filterLabel: getFilterLabel(tabIndex),
      })
    }
  }, [tabIndex, selectedDivisionUuid, selectedTeamUuid, onFilterChange])

  // フィルタリング済みユーザーリストを親に通知
  useEffect(() => {
    if (onFilteredUsersChange) {
      onFilteredUsersChange(filteredUsers)
    }
  }, [filteredUsers, onFilteredUsersChange])

  return {
    tabIndex,
    selectedDivisionUuid,
    selectedTeamUuid,
    setTabIndex,
    setSelectedDivisionUuid,
    setSelectedTeamUuid,
    divisions,
    teams,
    filteredUsers,
    requestingUsersCount,
    unrequestedUsersCount,
  }
}
